import { Component, DestroyRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateScopes } from '@cloud-twin-v2/cloud-twin-shared/core/translate-scopes';
import { SnapshotResponseDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { CtTabDetailParameterNames } from '@cloud-twin-v2/navigation/ct-tab-details-parameter-names';
import { SidePanelService } from '@cloud-twin-v2/side-panel/side-panel.service';
import { TabDetailPanelSettings } from '@common-modules/dependencies/navigation/tab-detail-component';
import { TabDetailPanelParameters } from '@common-modules/dependencies/navigation/tab-detail-panel-parameters';
import { DynamicLayoutComponent } from '@common-modules/dynamic-layout/dynamic-layout/dynamic-layout.component';
import { DynamicLayoutBuilderService } from '@common-modules/dynamic-layout/layouts/dynamic-layout-builder-service';
import { SimpleDynamicLayoutSettings } from '@common-modules/dynamic-layout/layouts/simple-dynamic-layout-settings';
import { SimpleDynamicLayoutSettingsItem } from '@common-modules/dynamic-layout/layouts/simple-dynamic-layout-settings-item';
import { DynamicLayoutSettings } from '@common-modules/dynamic-layout/models/dynamic-layout-settings';
import { DynamicLayoutStructure } from '@common-modules/dynamic-layout/models/dynamic-layout-structure';
import { LayoutNodeTypes } from '@common-modules/dynamic-layout/models/layout-node-types';
import { DynamicLayoutService } from '@common-modules/dynamic-layout/services/dynamic-layout.service';
import { GetValueSelector } from '@common-modules/dynamic-layout/state/generic/generic.selectors';
import { DropdownNavigationItem } from '@common-modules/navigation/models/dropdown-navigation-item';
import { StateAreas } from '@common-modules/redux/models/state-areas';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { BasePageComponent } from '@common-modules/shared/component/base-page.component';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { LocalizationHelperService } from '@common-modules/shared/localization/localization-helper.service';
import { IPendingChangesChecker } from '@common-modules/shared/pending-changes/models/pending-changes-checker';
import { Observable, map, of } from 'rxjs';
import { MODEL_VERSION_GRID_WIDGET_INSTANCE } from '../model-version-grid-widget/model-version-grid-widget.component';
import { ModelVersionScenariosGridWidgetComponent } from '../model-version-scenarios-grid-widget/model-version-scenarios-grid-widget.component';
import { MODEL_VERSION_SELECTOR_COMPONENT_INSTANCE } from '../model-version-selection-widget/model-version-selection-widget.component';
import { ModelStateFields } from '../models/model-state-fields';

const COMPONENT_SELECTOR = 'tc-model-version-page';
@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './model-version-page.component.html',
  styleUrl: './model-version-page.component.scss',
  providers: [ReduxStateService, DynamicLayoutService],
  standalone: false,
})
export class ModelVersionPageComponent
  extends BasePageComponent
  implements OnInit, IPendingChangesChecker, OnDestroy
{
  @ViewChild(DynamicLayoutComponent) set layoutComponent(component: DynamicLayoutComponent) {
    if (component) {
      this._dynamicLayoutService.registerLayout([component]);
    }
  }

  readonly T_SCOPE = `${TranslateScopes.ModelVersion}.${COMPONENT_SELECTOR}`;

  selectedSnapshots: SnapshotResponseDto[];
  selectedInstance: string;
  modelVersionDescriptionPrefix: string;
  readonly pageId: string = 'ModelVersionPage';

  private readonly _simpleSettings: SimpleDynamicLayoutSettings = {
    layoutKey: this.pageId,
    layoutArea: 'Main',
    scopeInstanceKeys: {
      [StateAreas.Generic]: 'shared',
    },
  };

  private readonly _stateWidgetSettings = new StateWidgetSettings({
    module: this._simpleSettings.layoutArea,
    page: this._simpleSettings.layoutKey,
    scopeInstanceKeys: this._simpleSettings.scopeInstanceKeys,
  });

  private readonly _selectorWidget: SimpleDynamicLayoutSettingsItem = {
    widgetName: 'ModelVersionSelectionWidgetComponent',
    titleKey: `${this.T_SCOPE}.widgets.model-selector`,
    id: MODEL_VERSION_SELECTOR_COMPONENT_INSTANCE,
  };

  private readonly _gridWidget: SimpleDynamicLayoutSettingsItem = {
    widgetName: 'ModelVersionGridWidgetComponent',
    titleKey: `${this.T_SCOPE}.widgets.snapshots-grid`,
    id: MODEL_VERSION_GRID_WIDGET_INSTANCE,
  };

  private readonly _defaultWidgets = [this._selectorWidget, this._gridWidget];

  private readonly _defaultStructure: DynamicLayoutStructure[] = [
    {
      type: LayoutNodeTypes.Column,
      content: [
        {
          type: LayoutNodeTypes.Row,
          content: [
            {
              type: LayoutNodeTypes.Stack,
              height: 90,
              width: 25,
              content: [
                {
                  type: LayoutNodeTypes.Component,
                  widgetInstanceKey: MODEL_VERSION_SELECTOR_COMPONENT_INSTANCE,
                },
              ],
            },
            {
              type: LayoutNodeTypes.Stack,
              height: 85,
              width: 75,
              content: [
                {
                  type: LayoutNodeTypes.Component,
                  widgetInstanceKey: MODEL_VERSION_GRID_WIDGET_INSTANCE,
                },
              ],
            },
          ],
        },
      ],
    },
  ];

  dynamicLayoutSettings: DynamicLayoutSettings;

  public get titleTranslationKey(): string {
    return `${this.T_SCOPE}.title`;
  }

  public get persistencyArea(): string {
    return this.pageCrud;
  }

  public get pageCrud(): string {
    return 'title';
  }

  public get navigations(): DropdownNavigationItem[] {
    return [];
  }

  constructor(
    private readonly _dynamicLayoutBuilderService: DynamicLayoutBuilderService,
    private readonly _dynamicLayoutService: DynamicLayoutService,
    private readonly _state: ReduxStateService,
    private readonly _sidePanel: SidePanelService,
    private readonly _destroyRef: DestroyRef,
    private readonly _localization: LocalizationHelperService
  ) {
    super();

    this._state.configure(this._stateWidgetSettings);
    this.hasRightPanel = true;
  }

  checkPendingChanges(key: string): Observable<unknown> {
    return of(null);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initialLoad();
    this.listenSnapshot();
    this.setTabSettings();
  }

  mapInitParameters(parameters: TabDetailPanelParameters) {}
  init(): void {}

  private setTabSettings(): void {
    this._localization.get(`${this.T_SCOPE}.widgets`).subscribe((translations) => {
      const settings = new TabDetailPanelSettings();
      settings.addComponent(
        ModelVersionScenariosGridWidgetComponent,
        translations['scenarios-grid']
      );
      this.modelVersionDescriptionPrefix = translations['model-version-description-title'];
      this._sidePanel.setTabSettings(settings);
    });
  }

  private initialLoad() {
    this.buildDynamicLayoutSettings().subscribe((settings) => {
      this.dynamicLayoutSettings = settings;
    });
  }

  private buildDynamicLayoutSettings(): Observable<DynamicLayoutSettings> {
    const simpleSettings = globalUtilsHelper.clone(this._simpleSettings, true);
    simpleSettings.items = this._defaultWidgets;

    return this._dynamicLayoutBuilderService.build(simpleSettings).pipe(
      map((dynamicLayoutSettings) => {
        dynamicLayoutSettings.showResetIcon = true;
        dynamicLayoutSettings.defaultStructure = this._defaultStructure;
        return dynamicLayoutSettings;
      })
    );
  }

  private listenSnapshot(): void {
    const selector = new GetValueSelector({
      fieldName: ModelStateFields.selectedSnapshot,
    });
    this._state
      .select<SnapshotResponseDto>(selector)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((snapshot: SnapshotResponseDto) => {
        const params = new TabDetailPanelParameters();
        if (snapshot && Object.keys(snapshot).length != 0) {
          const title = `${this.modelVersionDescriptionPrefix}: ${snapshot.name}`;
          params.addParameter(CtTabDetailParameterNames.scenariosGridRows, snapshot.scenarios);
          params.addParameter(CtTabDetailParameterNames.signalId, snapshot.snapshotId);
          params.addParameter(CtTabDetailParameterNames.elementName, title);
        }
        else{
          params.addParameter(CtTabDetailParameterNames.elementName, null);
          params.addParameter(CtTabDetailParameterNames.scenariosGridRows, []);
          params.addParameter(CtTabDetailParameterNames.signalId, null);
        }
        this._sidePanel.setTabParameters(params);
      });
  }
}
